import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { inputStep } from "@/constants/inputs";
import { flangeRatingOptions, type StrongbackTypeLocationSchema, strongbackTypeOptions } from "./schema";

type StrongbackFieldsRendererProps = {
  form: UseFormReturn<StrongbackTypeLocationSchema>;
  strongbackNumber: 1 | 2 | 3 | 4;
  showFields: boolean;
  measureUnitOptions: { value: string; title: string }[];
  npsOptions: { value: string | number; title: string }[];
  /** When true, forces Type to "Bar" and Distance to 0, and disables both fields. */
  locked?: boolean;
};

export function StrongbackFieldsRenderer({
  form,
  strongbackNumber,
  showFields,
  measureUnitOptions,
  npsOptions,
  locked = false,
}: StrongbackFieldsRendererProps) {
  const index = strongbackNumber - 1;
  const typeFieldName = `strongbacks.${index}.type` as const;
  const distanceFieldName = `strongbacks.${index}.distance` as const;
  const distanceUnitFieldName = `strongbacks.${index}.unit_for_distance` as const;
  const flangeNpsFieldName = `strongbacks.${index}.flange_nps` as const;
  const flangeRatingFieldName = `strongbacks.${index}.flange_rating` as const;
  const customDiameterFieldName = `strongbacks.${index}.custom_diameter` as const;
  const raisedFaceFieldName = `strongbacks.${index}.raised_face_od` as const;
  const boltHolesFieldName = `strongbacks.${index}.flange_num_bolt_holes` as const;
  const studSizeFieldName = `strongbacks.${index}.flange_stud_size` as const;
  const boltCircleFieldName = `strongbacks.${index}.flange_bolt_circle` as const;

  useEffect(() => {
    if (!locked) return;
    form.setValue(typeFieldName, "Bar");
    form.setValue(distanceFieldName, 0);
  }, [locked, form, typeFieldName, distanceFieldName]);

  if (!showFields) return null;

  const strongbackType = form.watch(typeFieldName);
  const isLoosering = strongbackType === "Loosering";
  const isNone = strongbackType === "none";
  const flangeNps = form.watch(flangeNpsFieldName);
  const isCustomFlange = flangeNps === "custom";
  const hasCustom = npsOptions.some((option) => option.value === "custom" || option.title === "custom");
  const npsOptionsWithCustom = hasCustom ? npsOptions : [{ title: "custom", value: "custom" }, ...npsOptions];

  const handleFlangeNpsChange = (value: string, onChange: (value: string) => void) => {
    onChange(value);

    if (value === "custom") {
      form.setValue(flangeRatingFieldName, undefined);
    } else {
      form.setValue(customDiameterFieldName, undefined);
      form.setValue(raisedFaceFieldName, undefined);
      form.setValue(boltHolesFieldName, undefined);
      form.setValue(studSizeFieldName, undefined);
      form.setValue(boltCircleFieldName, undefined);
    }
  };

  return (
    <div
      key={strongbackNumber}
      className="mt-2 flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-3 md:mt-3 md:p-4"
    >
      <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
        <p className="text-sm font-semibold text-gray-900">Strongback {strongbackNumber}</p>
        {strongbackType && strongbackType !== "none" && (
          <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-700">
            {strongbackType}
          </span>
        )}
      </div>

      {/* Type Dropdown */}
      <FormField
        control={form.control}
        name={typeFieldName}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Strongback {strongbackNumber} Type</FormLabel>
            <FormControl>
              <Select value={field.value || undefined} onValueChange={field.onChange} disabled={locked}>
                <SelectTrigger>
                  <SelectValue placeholder="Select strongback type" />
                </SelectTrigger>
                <SelectContent>
                  {strongbackTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Distance and Unit - only show if type is not "none" */}
      {!isNone && (
        <div className="flex flex-col gap-2 md:flex-row">
          <FormField
            control={form.control}
            name={distanceFieldName}
            render={({ field }) => (
              <FormItem className="flex flex-1 flex-col">
                <FormLabel>Strongback {strongbackNumber} Distance Away From Clamp</FormLabel>
                <FormControl>
                  <TextInput
                    type="number"
                    step={inputStep}
                    placeholder="Enter distance"
                    {...field}
                    disabled={locked}
                    onChange={(e) => {
                      const value = (e.target as HTMLInputElement).value;
                      field.onChange(value === "" ? undefined : Number.parseFloat(value));
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={distanceUnitFieldName}
            render={({ field }) => (
              <FormItem className="flex flex-1 flex-col">
                <FormLabel>Distance Unit</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || undefined}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {measureUnitOptions.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {/* Loosering fields - only show if type is "Loosering" */}
      {isLoosering && (
        <>
          <div className="flex flex-col gap-2 md:flex-row">
            <div className="flex-1">
              <FormField
                control={form.control}
                name={flangeNpsFieldName}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Strongback {strongbackNumber} Flange NPS</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value || undefined}
                        onValueChange={(value) => handleFlangeNpsChange(value, field.onChange)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select NPS" />
                        </SelectTrigger>
                        <SelectContent>
                          {npsOptionsWithCustom.map((option) => (
                            <SelectItem key={option.value} value={String(option.value)}>
                              {option.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {!isCustomFlange && (
              <div className="flex-1">
                <FormField
                  control={form.control}
                  name={flangeRatingFieldName}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Strongback {strongbackNumber} Flange Rating</FormLabel>
                      <FormControl>
                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select rating" />
                          </SelectTrigger>
                          <SelectContent>
                            {flangeRatingOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>

          {isCustomFlange && (
            <div className="grid gap-3 md:grid-cols-2">
              <FormField
                control={form.control}
                name={customDiameterFieldName}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Strongback {strongbackNumber} Custom Diameter</FormLabel>
                    <FormControl>
                      <TextInput
                        type="number"
                        step={inputStep}
                        placeholder="Enter custom diameter"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) => {
                          const value = (event.target as HTMLInputElement).value;
                          field.onChange(value === "" ? undefined : Number.parseFloat(value));
                        }}
                        endAdornment={<span className="ml-2 text-sm text-gray-500">in</span>}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={raisedFaceFieldName}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center justify-between">
                      <span>Strongback {strongbackNumber} Raised Face OD</span>
                      <span className="text-xs text-gray-500">Optional</span>
                    </FormLabel>
                    <FormControl>
                      <TextInput
                        type="number"
                        step={inputStep}
                        placeholder="Enter Raised Face OD"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) => {
                          const value = (event.target as HTMLInputElement).value;
                          field.onChange(value === "" ? undefined : Number.parseFloat(value));
                        }}
                        endAdornment={<span className="ml-2 text-sm text-gray-500">in</span>}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={boltHolesFieldName}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Strongback {strongbackNumber} Flange Num Bolt Holes</FormLabel>
                    <FormControl>
                      <TextInput
                        type="number"
                        step="1"
                        placeholder="Enter number of bolt holes"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) => {
                          const value = (event.target as HTMLInputElement).value;
                          field.onChange(value === "" ? undefined : Number.parseFloat(value));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={studSizeFieldName}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Strongback {strongbackNumber} Flange Stud Size</FormLabel>
                    <FormControl>
                      <TextInput
                        type="number"
                        step={inputStep}
                        placeholder="Enter stud size"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) => {
                          const value = (event.target as HTMLInputElement).value;
                          field.onChange(value === "" ? undefined : Number.parseFloat(value));
                        }}
                        endAdornment={<span className="ml-2 text-sm text-gray-500">in</span>}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={boltCircleFieldName}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Strongback {strongbackNumber} Flange Bolt Circle</FormLabel>
                    <FormControl>
                      <TextInput
                        type="number"
                        step={inputStep}
                        placeholder="Enter bolt circle"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) => {
                          const value = (event.target as HTMLInputElement).value;
                          field.onChange(value === "" ? undefined : Number.parseFloat(value));
                        }}
                        endAdornment={<span className="ml-2 text-sm text-gray-500">in</span>}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
